import { MouseEvent, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Menu,
  Pagination,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { useTranslation } from "react-i18next";
import styles from "./mobileDataGridList.module.css";
import { DataGridColumnSetting } from "../../../types/users";

const SEARCH_DEBOUNCE_MS = 350;

interface MobileDataGridListProps {
  rows: any[];
  columns: GridColDef[];
  loading: boolean;
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  filterModel: GridFilterModel;
  onFilterModelChange: (model: GridFilterModel) => void;
  columnVisibilityModel: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
  storedColumnSettings?: Record<string, DataGridColumnSetting>;
  onRowClick?: (id: string, row?: any) => void;
}

const humanizeFieldName = (field: string) =>
  field
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());

const getColumnLabel = (column: GridColDef, t: (key: string) => string) => {
  if (typeof column.headerName === "string" && column.headerName.trim().length > 0) {
    return t(column.headerName);
  }

  return t(humanizeFieldName(column.field));
};

const stringifyValue = (value: unknown): ReactNode => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return "-";
    }

    return value
      .map((item) => {
        if (item === null || item === undefined) {
          return "";
        }

        if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
          return String(item);
        }

        return JSON.stringify(item);
      })
      .filter(Boolean)
      .join(", ");
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
};

const getResolvedRawValue = (column: GridColDef, row: any) => {
  const baseValue = row?.[column.field];

  if (typeof column.valueGetter === "function") {
    return (column.valueGetter as any)(baseValue, row);
  }

  return baseValue;
};

const resolveColumnValue = (column: GridColDef, row: any): ReactNode => {
  const resolvedValue = getResolvedRawValue(column, row);

  if (typeof column.renderCell === "function") {
    const formattedValue =
      typeof column.valueFormatter === "function"
        ? (column.valueFormatter as any)(resolvedValue, row)
        : resolvedValue;

    const rendered = (column.renderCell as any)({
      id: row.id,
      field: column.field,
      row,
      value: resolvedValue,
      formattedValue,
      colDef: column,
      hasFocus: false,
      tabIndex: -1,
    });

    if (rendered !== undefined && rendered !== null) {
      return rendered;
    }
  }

  let normalizedValue: unknown = resolvedValue;

  if (typeof column.valueFormatter === "function") {
    normalizedValue = (column.valueFormatter as any)(normalizedValue, row);
  }

  if (typeof normalizedValue === "function") {
    return "-";
  }

  return stringifyValue(normalizedValue);
};

export default function MobileDataGridList({
  rows,
  columns,
  loading,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
  filterModel,
  onFilterModelChange,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  storedColumnSettings,
  onRowClick,
}: MobileDataGridListProps) {
  const { t } = useTranslation();
  const [columnsAnchorEl, setColumnsAnchorEl] = useState<null | HTMLElement>(null);
  const [searchInputValue, setSearchInputValue] = useState(
    filterModel.quickFilterValues?.[0] ?? ""
  );

  const isColumnVisible = useCallback(
    (field: string) => {
      if (field in columnVisibilityModel) {
        return columnVisibilityModel[field] !== false;
      }

      return storedColumnSettings?.[field]?.hidden !== true;
    },
    [columnVisibilityModel, storedColumnSettings]
  );

  const isColumnsMenuOpen = Boolean(columnsAnchorEl);

  const openColumnsMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setColumnsAnchorEl(event.currentTarget);
  };

  const closeColumnsMenu = () => {
    setColumnsAnchorEl(null);
  };

  const configurableColumns = useMemo(
    () =>
      columns.filter(
        (column) => column.field !== "__check__" && column.hideable !== false
      ),
    [columns]
  );

  const visibleColumns = useMemo(
    () =>
      columns.filter(
        (column) =>
          isColumnVisible(column.field) &&
          column.field !== "__check__"
      ),
    [columns, isColumnVisible]
  );

  const sortableColumns = useMemo(
    () => visibleColumns.filter((column) => column.sortable !== false),
    [visibleColumns]
  );

  const searchValue = filterModel.quickFilterValues?.[0] ?? "";

  useEffect(() => {
    setSearchInputValue(searchValue);
  }, [searchValue]);

  useEffect(() => {
    const trimmedValue = searchInputValue.trim();
    const nextQuickFilterValues = trimmedValue ? [searchInputValue] : [];
    const currentQuickFilterValues = filterModel.quickFilterValues ?? [];
    const isSameQuickFilter =
      nextQuickFilterValues.length === currentQuickFilterValues.length &&
      nextQuickFilterValues.every(
        (value, index) => value === currentQuickFilterValues[index]
      );

    if (isSameQuickFilter) {
      return;
    }

    const timeoutId = setTimeout(() => {
      onFilterModelChange({
        ...filterModel,
        quickFilterValues: nextQuickFilterValues,
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchInputValue, filterModel, onFilterModelChange]);

  const sortField = sortModel[0]?.field || "";
  const sortDirection = sortModel[0]?.sort || "asc";

  const totalPages = Math.max(1, Math.ceil(rowCount / paginationModel.pageSize));
  const currentPage = paginationModel.page + 1;

  const handleSortFieldChange = (event: SelectChangeEvent<string>) => {
    const field = event.target.value;

    if (!field) {
      onSortModelChange([]);
      return;
    }

    onSortModelChange([{ field, sort: sortDirection }]);
  };

  const toggleSortDirection = () => {
    if (!sortField) {
      if (sortableColumns.length > 0) {
        onSortModelChange([{ field: sortableColumns[0].field, sort: "asc" }]);
      }
      return;
    }

    onSortModelChange([
      {
        field: sortField,
        sort: sortDirection === "asc" ? "desc" : "asc",
      },
    ]);
  };

  return (
    <Box className={styles.mobileWrapper}>
      {(sortableColumns.length > 0 || configurableColumns.length > 0) && (
        <Box className={styles.controls}>
          <TextField
            value={searchInputValue}
            onChange={(event) => setSearchInputValue(event.target.value)}
            size="small"
            className={styles.searchField}
            label={t("Search")}
            placeholder={t("Search")}
          />

          {sortableColumns.length > 0 && (
            <>
              <FormControl size="small" className={styles.sortSelect}>
                <InputLabel>{t("Sort by")}</InputLabel>
                <Select
                  value={sortField}
                  label={t("Sort by")}
                  onChange={handleSortFieldChange}
                >
                  <MenuItem value="">{t("No sorting")}</MenuItem>
                  {sortableColumns.map((column) => (
                    <MenuItem key={column.field} value={column.field}>
                      {getColumnLabel(column, t)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                onClick={toggleSortDirection}
                size="small"
                className={styles.sortDirectionButton}
                aria-label={t("Direction")}
                disabled={!sortField && sortableColumns.length === 0}
              >
                <SwapVertIcon fontSize="small" />
              </IconButton>
            </>
          )}

          {configurableColumns.length > 0 && (
            <>
              <Tooltip title={t("Columns")}>
                <IconButton
                  size="small"
                  className={styles.columnsButton}
                  onClick={openColumnsMenu}
                  aria-label={t("Columns")}
                >
                  <ViewColumnIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={columnsAnchorEl}
                open={isColumnsMenuOpen}
                onClose={closeColumnsMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <Box className={styles.columnsMenuContent}>
                  <FormGroup>
                    {configurableColumns.map((column) => (
                      <FormControlLabel
                        key={column.field}
                        control={
                          <Checkbox
                            checked={isColumnVisible(column.field)}
                            onChange={(_event, checked) => {
                              if (!onColumnVisibilityModelChange) {
                                return;
                              }

                              onColumnVisibilityModelChange({
                                ...columnVisibilityModel,
                                [column.field]: checked,
                              });
                            }}
                            size="small"
                          />
                        }
                        label={getColumnLabel(column, t)}
                      />
                    ))}
                  </FormGroup>
                </Box>
              </Menu>
            </>
          )}
        </Box>
      )}

      <Box className={styles.cards}>
        {loading ? (
          <Box className={styles.loading}>
            <CircularProgress size={26} />
          </Box>
        ) : (
          <>
            {rows.length === 0 && <Box className={styles.empty}>{t("No data")}</Box>}

            {rows.map((row) => {
              const rowId = String(row.id);

              return (
                <Box
                  key={rowId}
                  className={`${styles.card} ${onRowClick ? styles.clickable : ""}`}
                  onClick={onRowClick ? () => onRowClick(rowId, row) : undefined}
                  role={onRowClick ? "button" : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick
                      ? (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            onRowClick(rowId, row);
                          }
                        }
                      : undefined
                  }
                >
                  {visibleColumns.map((column) => (
                    <Box key={`${rowId}-${column.field}`} className={styles.rowLine}>
                      <Typography className={styles.label}>{getColumnLabel(column, t)}</Typography>
                      <Box className={styles.value}>{resolveColumnValue(column, row)}</Box>
                    </Box>
                  ))}
                </Box>
              );
            })}
          </>
        )}
      </Box>

      {rowCount > 0 && (
        <Box className={styles.footer}>
          <Box className={styles.pageInfo}>
            {`${paginationModel.page * paginationModel.pageSize + 1}-${Math.min(
              (paginationModel.page + 1) * paginationModel.pageSize,
              rowCount
            )} / ${rowCount}`}
          </Box>
          <Box className={styles.pagination}>
            <Typography variant="body2">{t("Rows per page")}: {paginationModel.pageSize}</Typography>
            <Pagination
              count={totalPages}
              page={Math.min(currentPage, totalPages)}
              onChange={(_event, page) =>
                onPaginationModelChange({ ...paginationModel, page: page - 1 })
              }
              size="small"
              color="primary"
              shape="rounded"
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}
